import { useEffect, useState, useCallback } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useActions } from "app/utils/hooks";
import {
  fetchSubscriptions,
  updateRenewal,
  clearSubscription,
} from "app/actions/subscription";
import { fetchSelf } from "app/actions/user";
import { RootState } from "typedefs";
import moment from "moment";
import { SubscriptionModel } from "app/models";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

export const useSubscriptionModel = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const { data: subscriptions, pending } = useSelector(
    (state: RootState) => state.subscription,
    shallowEqual,
  );
  const user = useSelector(
    (state: RootState) => state.user?.self,
    shallowEqual,
  );

  const [cancelModal, setCancelModal] = useState(false);
  const [cancelProTrialModal, setCancelProTrialModal] = useState(false);

  const subscriptionActions = useActions({
    fetchSubscriptions,
    fetchSelf,
    updateRenewal,
    clearSubscription,
  });

  const toggleCancelModal = useCallback(() => {
    setCancelModal((prev) => !prev);
  }, []);

  const toggleCancelProTrialModal = useCallback(() => {
    setCancelProTrialModal((prev) => !prev);
  }, []);

  useEffect(() => {
    subscriptionActions.fetchSubscriptions();
    return () => {
      subscriptionActions.clearSubscription();
    };
  }, [subscriptionActions]);

  const onUpdateRenewal = async ({
    product,
    enable,
  }: {
    product: SubscriptionModel;
    enable: boolean;
  }) => {
    try {
      setLoading(true);
      await subscriptionActions.updateRenewal({ product, enable });
      history.push("/");
      toast.success(t("Subscription renewed successfully."));
    } catch (err) {
      // The API answers a refused toggle with a human readable message
      // (a licensed seat, or a coach without a Stripe customer of his own).
      toast.error(
        err?.message ||
          t("Something went wrong. Please try again later or contact support."),
      );
    } finally {
      setLoading(false);
    }
  };

  const isExpired = useCallback((subscription: SubscriptionModel) => {
    if (!subscription.expiresAt) {
      return false;
    }
    return subscription.expiresAt < new Date().toISOString();
  }, []);

  const isCanceled = (subscription: SubscriptionModel) => {
    return subscription?.canceled;
  };

  const canCancel = useCallback(
    (subscription: SubscriptionModel) => {
      return !isExpired(subscription) && !user?.plan?.canceledAt;
    },
    [isExpired, user],
  );

  const formatDate = useCallback((date: string | undefined | null) => {
    if (!date) {
      return "";
    }
    return moment(date).format("MMM DD, YYYY");
  }, []);

  const refetchSubscriptions = useCallback(() => {
    subscriptionActions.fetchSubscriptions();
    subscriptionActions.fetchSelf();
  }, [subscriptionActions]);

  return {
    subscriptions,
    pending,
    user,
    cancelModal,
    cancelProTrialModal,
    toggleCancelModal,
    toggleCancelProTrialModal,
    isExpired,
    isCanceled,
    canCancel,
    formatDate,
    refetchSubscriptions,
    onUpdateRenewal,
    loading,
  };
};
